import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { HANDBOOK_INITIAL_LOAD_COUNT, HANDBOOK_SHOW_MORE_LIMIT } from "pages/HandBook/const";
import { HANDBOOK_TEMPLATE_LVL } from "pages/HandBook/model/slice";
import {
  getHandbookTemplateSections,
  getHandbookTemplateSectionsMore,
  getHandbookTemplates,
} from "pages/HandBook/model/thunks";
import { IHandBookTemplateSection } from "pages/HandBook/model/types";
import { handbooksModifyWithEditings } from "pages/HandBook/utils/handbooksModifyWithEditings";

import { ICommonColumn } from "../templates/HandBooksTemplates";
import { useTypedSelector } from "app/store/typedUseSelector";

import { IIdAndName } from "types/idAndName";

import { generateStorageKey } from "utils/helpers/generateStorageKey";

export const useHandbookTemplateSections = () => {
  const history = useHistory();
  const { templateId, sectionId } = useParams<{ templateId: string; sectionId?: string }>();
  const isSubSections = !!sectionId;
  const [filter, setFilter] = useState<Record<string, string>>(() => ({
    template_id: templateId,
  }));
  // Фильтр для подразделов обрабатывается на фронте без запроса на сервер
  const [subsectionNameFilter, setSubsectionNameFilter] = useState<string>("");
  const hasListFilters = Object.keys(filter).length > 1 || !!subsectionNameFilter.trim();
  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey(restFilter as any);
  const moreKey = generateStorageKey({
    ...restFilter,
    type: "moreTemplateSections",
  } as any);
  const dispatch = useDispatch();
  const cacheKey = useTypedSelector((state) => state.handBookReducer.invalidationKey);
  // @ts-ignore | Ключ всех шаблонов – пустой фильтр
  const templates = useTypedSelector((state) => state.handBookReducer.templates)[generateStorageKey({})];
  const sections = useTypedSelector((state) => state.handBookReducer.templateSections)[key];
  const editings = useTypedSelector((state) => state.handBookReducer.templateEditings)[HANDBOOK_TEMPLATE_LVL.SECTIONS];
  const isEditing = useTypedSelector((state) => state.handBookReducer.templateEditings.isEditing);

  const { currentTemplate, currentSection } = useMemo(() => {
    const currentTemplate = templateId ? templates?.results?.find((t) => +t.id === +templateId) : null;
    const currentSection = sectionId ? sections?.results?.find((s) => +s.id === +sectionId) : null;
    return { currentTemplate, currentSection };
  }, [sectionId, templateId, sections, templates]);

  const entries = useMemo(() => {
    if (isSubSections) {
      const children = currentSection?.children || [];
      return { results: children, count: children.length };
    }
    return sections;
  }, [sections, isSubSections, currentSection]);

  const isLoading = useTypedSelector((state) => state.handBookReducer.loadings)[key];
  const isLoadingMore = useTypedSelector((state) => state.handBookReducer.loadings)[moreKey];
  const isLoadingApplyEditings = useTypedSelector((state) => state.handBookReducer.loadings)[
    "applyEditingHandbookTemplates"
  ];

  useEffect(() => {
    if (!isSubSections) {
      // Сброс фильтра подразделов при переключении между разделами
      setSubsectionNameFilter("");
    }
  }, [isSubSections]);

  const displaySections = useMemo(() => {
    if (!entries?.results) return entries?.results;
    let candidates = entries?.results;
    if (isSubSections && !!subsectionNameFilter.trim()) {
      const searchPart = subsectionNameFilter.toLowerCase().trim();
      candidates = candidates.filter((s) => s.name.toLowerCase().includes(searchPart));
    }
    return handbooksModifyWithEditings(candidates, editings);
  }, [entries?.results, editings, subsectionNameFilter, isSubSections]);

  useEffect(() => {
    if (!templates?.results?.length) {
      dispatch(getHandbookTemplates({ limit: HANDBOOK_INITIAL_LOAD_COUNT }));
    }
    dispatch(
      getHandbookTemplateSections({
        ...filter,
        limit: Math.max(sections?.results?.length ?? HANDBOOK_SHOW_MORE_LIMIT, HANDBOOK_SHOW_MORE_LIMIT) + 1,
      })
    );
  }, [cacheKey, filter]);

  const onPagination = () => {
    if (isSubSections) return;
    dispatch(
      getHandbookTemplateSectionsMore({ ...filter, offset: sections?.results?.length, limit: HANDBOOK_SHOW_MORE_LIMIT })
    );
  };

  const onBack = () => {
    // Убираем крайний численный параметр
    history.replace(history.location.pathname.replace(/\/\d+\/?$/, ""));
  };

  const columns: ICommonColumn<IHandBookTemplateSection | IIdAndName>[] = [
    {
      header: {
        title: isSubSections ? "Подраздел" : "Раздел",
        // Фильтр для подразделов обрабатывается на фронте без запроса на сервер
        filterName: isSubSections ? "_subsection_name" : "name",
      },
      render: (item) => item.name,
    },
  ];

  const onRowClick = (id: number) => {
    if (isSubSections) {
      history.push(`./${sectionId}/${id}`);
    } else {
      history.push(`./${templateId}/${id}`);
    }
  };

  const onChangeFilter = useCallback((name: string, value: string) => {
    if (name === "_subsection_name") {
      setSubsectionNameFilter(value);
    } else {
      setFilter((prev) => ({ ...prev, [name]: value }));
    }
  }, []);

  const total = useMemo(
    () =>
      (entries?.count || 0) -
      Object.entries(editings.deletions).filter(([id, isDeleted]) => isDeleted && Number(id) > 0).length,
    [entries?.count, editings.deletions]
  );

  return {
    hasListFilters,
    isSubSections,
    displaySections,
    isLoading,
    isLoadingMore,
    isLoadingApplyEditings,
    isEditing,
    total,
    filter,
    onChangeFilter,
    onRowClick,
    onPagination,
    columns,
    onBack,
    currentTemplate,
    currentSection,
    templateId,
    sectionId,
  };
};
